/**
 * useLayoutTemplates Hook
 * 
 * Hook for managing futuristic layout templates.
 * Provides queries and mutations for listing, applying, and configuring layouts.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import type { 
  LayoutTemplate, 
  LayoutConfig, 
  LayoutSettings, 
  FuturisticLayoutType,
  LayoutCategory 
} from '../types/layout.types';

// API Response types
interface LayoutTemplateResponse extends Omit<LayoutTemplate, 'defaultConfig'> {
  defaultConfig: Record<string, any>;
}

interface LayoutTemplatesByCategoryResponse {
  BASIC: LayoutTemplateResponse[];
  NAVIGATION: LayoutTemplateResponse[];
  VISUAL_EFFECTS: LayoutTemplateResponse[];
  THREE_D_DEPTH: LayoutTemplateResponse[];
  ANIMATIONS: LayoutTemplateResponse[];
  SCIFI: LayoutTemplateResponse[];
}

interface LayoutConfigResponse {
  layoutType: FuturisticLayoutType;
  layoutConfig: Record<string, any>;
}

interface UpdateLayoutConfigDto {
  layoutType: FuturisticLayoutType;
  settings: Partial<LayoutSettings>;
}

// Query keys
const LAYOUT_TEMPLATES_KEY = 'layout-templates';
const LAYOUT_CONFIG_KEY = 'layout-config';

/**
 * List all layout templates
 */
export function useLayoutTemplates(category?: LayoutCategory) {
  // Check if user is authenticated
  const hasToken = typeof window !== 'undefined' && !!localStorage.getItem('accessToken');
  
  return useQuery({
    queryKey: [LAYOUT_TEMPLATES_KEY, category],
    queryFn: async () => {
      const params = category ? { category } : {};
      const { data } = await api.get<LayoutTemplateResponse[]>('/layout-templates', { params });
      return data;
    },
    enabled: hasToken, // Only fetch if authenticated
  });
}

/**
 * Get layout templates grouped by category
 */
export function useLayoutTemplatesByCategory() {
  // Check if user is authenticated
  const hasToken = typeof window !== 'undefined' && !!localStorage.getItem('accessToken');
  
  return useQuery({
    queryKey: [LAYOUT_TEMPLATES_KEY, 'by-category'],
    queryFn: async () => {
      const { data } = await api.get<LayoutTemplatesByCategoryResponse>('/layout-templates/by-category');
      return data;
    },
    enabled: hasToken,
  });
}

/**
 * Get template count by category
 */
export function useLayoutTemplateCount() {
  // Check if user is authenticated
  const hasToken = typeof window !== 'undefined' && !!localStorage.getItem('accessToken');
  
  return useQuery({
    queryKey: [LAYOUT_TEMPLATES_KEY, 'count'],
    queryFn: async () => {
      const { data } = await api.get<Record<LayoutCategory, number>>('/layout-templates/count-by-category');
      return data;
    },
    enabled: hasToken,
  });
}

/**
 * Get a single layout template by ID
 */
export function useLayoutTemplate(id: string) {
  return useQuery({
    queryKey: [LAYOUT_TEMPLATES_KEY, id],
    queryFn: async () => {
      const { data } = await api.get<LayoutTemplateResponse>(`/layout-templates/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

/**
 * Get current layout configuration for a tenant
 */
export function useLayoutConfig(tenantId: string) {
  return useQuery({
    queryKey: [LAYOUT_CONFIG_KEY, tenantId],
    queryFn: async () => {
      const { data } = await api.get<LayoutConfigResponse>(`/themes/${tenantId}/layout-config`);
      return data;
    },
    enabled: !!tenantId,
  });
}

/**
 * Apply a layout template to a tenant
 */
export function useApplyLayoutTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ tenantId, templateId }: { tenantId: string; templateId: string }) => {
      const { data } = await api.post(`/themes/${tenantId}/apply-layout/${templateId}`);
      return data;
    },
    onSuccess: (_, { tenantId }) => {
      queryClient.invalidateQueries({ queryKey: [LAYOUT_CONFIG_KEY, tenantId] });
      queryClient.invalidateQueries({ queryKey: ['tenant-theme', tenantId] });
    },
  });
}

/**
 * Update layout configuration for a tenant
 */
export function useUpdateLayoutConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ tenantId, config }: { tenantId: string; config: UpdateLayoutConfigDto }) => {
      const { data } = await api.put(`/themes/${tenantId}/layout-config`, config);
      return data;
    },
    onSuccess: (_, { tenantId }) => {
      queryClient.invalidateQueries({ queryKey: [LAYOUT_CONFIG_KEY, tenantId] });
      queryClient.invalidateQueries({ queryKey: ['tenant-theme', tenantId] });
    },
  });
}

/**
 * Create a new layout template (Super Admin)
 */
export function useCreateLayoutTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (template: Omit<LayoutTemplate, 'id' | 'createdAt' | 'updatedAt'>) => {
      const { data } = await api.post<LayoutTemplateResponse>('/layout-templates', template);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LAYOUT_TEMPLATES_KEY] });
    },
  });
}

/**
 * Update a layout template (Super Admin)
 */
export function useUpdateLayoutTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...template }: Partial<LayoutTemplate> & { id: string }) => {
      const { data } = await api.put<LayoutTemplateResponse>(`/layout-templates/${id}`, template);
      return data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [LAYOUT_TEMPLATES_KEY] });
      queryClient.invalidateQueries({ queryKey: [LAYOUT_TEMPLATES_KEY, id] });
    },
  });
}

/**
 * Delete a layout template (Super Admin)
 */
export function useDeleteLayoutTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/layout-templates/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LAYOUT_TEMPLATES_KEY] });
    },
  });
}

/**
 * Helper hook to get layout metadata
 */
export function useLayoutMetadata() {
  const { data: templates, isLoading } = useLayoutTemplatesByCategory();

  const getTemplateByType = (type: FuturisticLayoutType): LayoutTemplateResponse | undefined => {
    if (!templates) return undefined;

    for (const category of Object.values(templates)) {
      const found = category.find((t: LayoutTemplateResponse) => t.layoutType === type);
      if (found) return found;
    }
    return undefined;
  };

  const getFuturisticTemplates = (): LayoutTemplateResponse[] => {
    if (!templates) return [];

    return [
      ...templates.NAVIGATION,
      ...templates.VISUAL_EFFECTS,
      ...templates.THREE_D_DEPTH,
      ...templates.ANIMATIONS,
      ...templates.SCIFI,
    ];
  };

  const getBasicTemplates = (): LayoutTemplateResponse[] => {
    return templates?.BASIC || [];
  };

  return {
    templates,
    isLoading,
    getTemplateByType,
    getFuturisticTemplates,
    getBasicTemplates,
  };
}

export default useLayoutTemplates;
